import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import InputField from "@medoma/components/InputField/InputField";
import BinIcon from "@medoma/components/icons/BinIcon";
import Form from "@/components/Form/Form";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import {
  categoriesQueryOptions,
  createCategory,
  deleteCategory,
  updateCategoryName,
} from "@/api/Equipment";
import { deducedError } from "@/Utils/ErrorUtils";
import styles from "./CategoryForm.module.scss";

type CategoryFormFields = {
  name: string;
};

type CategoryFormProps =
  | { mode: "create"; id?: never; currentName?: never }
  | { mode: "edit"; id: string; currentName: string };

const CategoryForm = (props: CategoryFormProps) => {
  const { mode, id, currentName } = props;
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { confirm } = useDialog();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<CategoryFormFields>({
    defaultValues: { name: currentName ?? "" },
  });

  const { mutate: createCategoryMutation, isPending: isPendingCreate } =
    useMutation({
      mutationFn: (name: string) => createCategory({ name }),
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        void navigate("..");
      },
      onError: (error) => {
        setError("root.server", {
          message: `${t`Kunde inte skapa kategori.`} ${deducedError(error)}`,
        });
      },
    });

  const { mutate: updateCategoryMutation, isPending: isPendingUpdate } =
    useMutation({
      mutationFn: ({
        categoryId,
        name,
      }: {
        categoryId: string;
        name: string;
      }) => updateCategoryName({ categoryId, name }),
      onSuccess: async () => {
        await queryClient.invalidateQueries();
      },
      onError: (error) => {
        setError("root.server", {
          message: `${t`Kunde inte byta namn på kategorin.`} ${deducedError(error)}`,
        });
      },
    });

  const { mutate: deleteCategoryMutation, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (categoryId: string) => deleteCategory(categoryId),
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        void navigate("..");
      },
      onError: (error) => {
        setError("root.server", {
          message: `${t`Kunde inte ta bort kategori.`} ${deducedError(error)}`,
        });
      },
    });

  const isPendingMutation =
    isPendingCreate || isPendingUpdate || isPendingDelete;

  const categoryName = currentName ?? "";
  const confirmAndDelete = async () => {
    const confirmed = await confirm({
      title: t`Ta bort kategori`,
      message: t`${categoryName} kommer att tas bort. Detta går inte att ångra.`,
      confirmLabel: t`Ta bort`,
    });
    if (confirmed && mode === "edit") {
      deleteCategoryMutation(id);
    }
  };

  const validateAndSubmit = handleSubmit((values) => {
    const trimmedName = values.name.trim();
    if (mode === "create") {
      createCategoryMutation(trimmedName);
    } else {
      updateCategoryMutation({ categoryId: id, name: trimmedName });
    }
  });

  return (
    <Form onSubmit={validateAndSubmit}>
      <InputField
        label={t`Namn`}
        showOptionalLabel={false}
        autoComplete="off"
        errorMessage={errors.name?.message}
        {...register("name", {
          required: { value: true, message: t`Ange ett namn` },
        })}
      />

      {errors.root?.server?.message ? (
        <ErrorMessage message={errors.root.server.message} />
      ) : null}

      {isPendingCreate ? (
        <Loading message={t`Skapar kategori`} />
      ) : isPendingUpdate ? (
        <Loading message={t`Sparar`} />
      ) : isPendingDelete ? (
        <Loading message={t`Tar bort kategori`} />
      ) : (
        <div className={styles.actions}>
          <FilledButton type="submit" disabled={isPendingMutation}>
            <Trans>Spara</Trans>
          </FilledButton>
          {mode === "edit" ? (
            <PlainButton
              type="button"
              disabled={isPendingMutation}
              onClick={confirmAndDelete}
            >
              <BinIcon />
              <Trans>Ta bort</Trans>
            </PlainButton>
          ) : null}
        </div>
      )}
    </Form>
  );
};

export const NewCategoryForm = () => {
  const navigate = useNavigate();
  return (
    <>
      <header className={styles.header}>
        <Heading level="h1">
          <Trans>Skapa kategori</Trans>
        </Heading>
        <CloseButton close={() => navigate("..")} />
      </header>
      <CategoryForm key="new" mode="create" />
    </>
  );
};

export const EditCategoryForm = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { id } = z.object({ id: z.string() }).parse(useParams());

  const {
    data: categories,
    isPending,
    isError,
    error,
  } = useQuery(categoriesQueryOptions());

  if (isPending) {
    return <Loading message={t`Hämtar kategori`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta kategori.`} ${deducedError(error)}`}
      />
    );
  }

  const category = categories.find((c) => c.id === id);

  if (!category) {
    return <ErrorMessage message={t`Kategorin hittades inte.`} />;
  }

  return (
    <>
      <header className={styles.header}>
        <Heading level="h1">{category.name}</Heading>
        <CloseButton close={() => navigate("..")} />
      </header>
      <CategoryForm key={id} mode="edit" id={id} currentName={category.name} />
    </>
  );
};
